import { useEffect, useState } from 'react';
import { hasScope } from '@nyce/admin-kit/api';
import { SCOPES } from '../../api';
import { useHubLogin } from '../../session';
import { loadTargets, type AuthTargetInfo } from '../../identity/session';
import type { Issue } from '../../pages/dashboard/shared';
import {
  automationGw,
  type Agent,
  type AgentInfo,
  type Route,
  type TunnelStatus,
  type UpdateInfo,
} from '../api/client';
import { routeTraffic, type RouteTraffic } from '../pages/Traffic';
import { agPath, agentInfos, isOutdated, tunnelOf } from '../shared';

// The automation gateway per environment, for the Automation group dashboard and the main dashboard: agents with their
// tunnel, version and disks, when offline ones were last seen, and the last 24 h of route traffic.

export interface AutomationSummary {
  target: AuthTargetInfo;
  /** Why the environment could not be read at all. */
  error: string | null;
  agents: Agent[];
  routes: Route[];
  tunnels: TunnelStatus[];
  latest: UpdateInfo | null;
  infos: Map<string, AgentInfo | null>;
  /** Offline agents: when their last connection ended (from the connection history), or null. */
  lastSeen: Map<string, string | null>;
  traffic: RouteTraffic[];
  partErrors: string[];
}

/** Below this share of free space a disk is flagged. */
export const LOW_DISK = 0.1;

async function summarize(t: AuthTargetInfo): Promise<AutomationSummary> {
  const api = automationGw(t.id);
  const base: AutomationSummary = {
    target: t,
    error: null,
    agents: [],
    routes: [],
    tunnels: [],
    latest: null,
    infos: new Map(),
    lastSeen: new Map(),
    traffic: [],
    partErrors: [],
  };
  const partErrors: string[] = [];
  const soft = <T>(p: Promise<T>, what: string) =>
    p.catch((e: Error) => {
      partErrors.push(`${what}: ${e.message}`);
      return null;
    });
  let agents: Agent[], tunnels: TunnelStatus[];
  try {
    [agents, tunnels] = await Promise.all([api.agents(), api.tunnels()]);
  } catch (e) {
    return { ...base, error: (e as Error).message };
  }
  const offline = agents.filter((a) => !tunnelOf(tunnels, a.clientId)?.isConnected);
  const [routes, latest, stats, infos, seen] = await Promise.all([
    soft(api.routes(), 'routes'),
    soft(api.latest(), 'latest version'),
    soft(api.statistics(24), 'traffic'),
    agentInfos(api, tunnels),
    Promise.all(
      offline.map(async (a) => {
        const h = await api.history(a.clientId, 30).catch(() => []);
        const last = h
          .map((x) => x.disconnectedAt ?? x.connectedAt)
          .sort()
          .at(-1);
        return [a.clientId, last ?? null] as const;
      })
    ),
  ]);
  return {
    ...base,
    agents,
    tunnels,
    routes: routes ?? [],
    latest,
    infos,
    lastSeen: new Map(seen),
    traffic: (stats?.routes ?? [])
      .map((r) => routeTraffic(r, 3_600_000))
      .sort((a, b) => b.toAgent + b.fromAgent - (a.toAgent + a.fromAgent)),
    partErrors,
  };
}

export const connectedCount = (s: AutomationSummary) =>
  s.agents.filter((a) => tunnelOf(s.tunnels, a.clientId)?.isConnected).length;

export const outdatedAgents = (s: AutomationSummary) =>
  s.agents.filter((a) => {
    const t = tunnelOf(s.tunnels, a.clientId);
    return !!t && isOutdated(s.infos.get(t.clientId)?.version, s.latest?.version);
  });

/** Agents with at least one enabled route: offline means something in a warehouse is cut off. */
const inUse = (s: AutomationSummary, a: Agent) => s.routes.some((r) => r.clientId === a.id && r.enabled);

/** What needs a look: offline agents with routes, low disks, outdated agents. */
export function automationIssues(list: AutomationSummary[]): Issue[] {
  const out: Issue[] = [];
  for (const s of list) {
    const env = s.target.label;
    const link = (path: string) => agPath(path, s.target.id);
    if (s.error) {
      out.push({
        key: `ag:${s.target.id}`,
        status: 'error',
        name: `Automation gateway ${env}`,
        what: 'environment',
        detail: s.error,
        to: link('/automation'),
      });
      continue;
    }
    for (const a of s.agents) {
      const t = tunnelOf(s.tunnels, a.clientId);
      if (!t?.isConnected) {
        if (!inUse(s, a)) continue;
        const seen = s.lastSeen.get(a.clientId);
        out.push({
          key: `ag:${s.target.id}:offline:${a.id}`,
          status: 'down',
          name: a.displayName,
          what: `agent · ${env}`,
          detail: seen
            ? `offline, last connected ${new Date(seen).toLocaleString()}`
            : 'offline, not connected in 30 days',
          to: link(`/automation/agents/${encodeURIComponent(a.id)}`),
        });
        continue;
      }
      if (a.configSource === 'current')
        out.push({
          key: `ag:${s.target.id}:config:${a.id}`,
          status: 'warn',
          name: a.displayName,
          what: `agent · ${env}`,
          detail: 'config only in current/: it would be lost on the next update',
          to: link(`/automation/agents/${encodeURIComponent(a.id)}`),
        });
      if (a.lastAuthError && a.authMode !== 'legacy')
        out.push({
          key: `ag:${s.target.id}:auth:${a.id}`,
          status: a.authMode === 'auth' ? 'error' : 'warn',
          name: a.displayName,
          what: `agent AUTH · ${env}`,
          detail: a.lastAuthError,
          to: link(`/automation/agents/${encodeURIComponent(a.id)}`),
        });
      const info = s.infos.get(t.clientId);
      for (const d of info?.diskInfo ?? [])
        if (d.totalSizeMB > 0 && d.availableFreeMB / d.totalSizeMB < LOW_DISK)
          out.push({
            key: `ag:${s.target.id}:disk:${a.id}:${d.name}`,
            status: 'warn',
            name: `${a.displayName} · ${d.name}`,
            what: `agent disk · ${env}`,
            detail: `${Math.round((d.availableFreeMB / d.totalSizeMB) * 100)} % free`,
            to: link(`/automation/agents/${encodeURIComponent(a.id)}`),
          });
    }
    const old = outdatedAgents(s);
    if (old.length)
      out.push({
        key: `ag:${s.target.id}:outdated`,
        status: 'warn',
        name: `${old.length} agent${old.length === 1 ? '' : 's'}`,
        what: `agent versions · ${env}`,
        detail: `older than ${s.latest?.version}: ${old.map((a) => a.displayName).join(', ')}`,
        to: link('/automation/agents?state=outdated'),
      });
  }
  return out;
}

const TTL = 60_000;
let cache: { at: number; data: Promise<AutomationSummary[]> } | null = null;

/** Every environment with an automation gateway (callers with ophub:automation:read), cached for a minute. */
export function useAutomationSummary() {
  const enabled = hasScope(useHubLogin()?.scopes, SCOPES.automationRead);
  const [state, setState] = useState<{
    data: AutomationSummary[] | null;
    loading: boolean;
    error: string | null;
  }>({
    data: null,
    loading: enabled,
    error: null,
  });
  const [round, setRound] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    if (round > 0 || !cache || Date.now() - cache.at > TTL)
      cache = {
        at: Date.now(),
        data: loadTargets().then((list) => Promise.all(list.filter((t) => t.automationGw).map(summarize))),
      };
    cache.data.then(
      (data) => alive && setState({ data, loading: false, error: null }),
      (e) => alive && setState({ data: null, loading: false, error: `Automation: ${(e as Error).message}` })
    );
    return () => {
      alive = false;
    };
  }, [enabled, round]);
  return { ...state, enabled, reload: () => setRound((r) => r + 1) };
}

/** Test hook. */
export const resetAutomationSummary = () => {
  cache = null;
};

/** Where the move to AUTH stands: agents per sign-in mode, and those it cannot reach remotely. */
export function authProgress(s: AutomationSummary) {
  const by = (m: string) => s.agents.filter((a) => (a.authMode ?? 'legacy') === m).length;
  return {
    secret: by('legacy'),
    enrolled: s.agents.filter((a) => a.authMode === 'both' && a.lastAuthMethod !== 'bearer').length,
    onAuth: s.agents.filter(
      (a) => a.authMode === 'auth' || (a.authMode === 'both' && a.lastAuthMethod === 'bearer')
    ).length,
    authOnly: by('auth'),
    siteVisit: s.agents.filter((a) => a.remoteUpdatable === false).length,
  };
}
